import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Banner that offers existing users their one-time 48-hour Pro trial.
 *
 * Shown when the user is signed in, is NOT currently on Pro, and has never
 * used their trial (`has_used_trial === false`). New signups already get
 * the trial auto-claimed at register time, so this banner is only for
 * pre-existing accounts (or anyone whose Pro window has since expired
 * without ever having used the trial).
 */
export default function ProTrialBanner() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user) return null;
  if (user.is_pro) return null;
  if (user.has_used_trial) return null;
  if (dismissed) return null;

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/claim-trial");
      const hours = data?.trial_hours || 48;
      // Reflect the new Pro state immediately so gated features unlock.
      if (typeof setUser === "function") {
        setUser((cur) => cur ? {
          ...cur,
          is_pro: true,
          has_used_trial: true,
          subscription_expires_at: data?.subscription_expires_at || cur.subscription_expires_at,
          trial_started_at: data?.trial_started_at || cur.trial_started_at,
        } : cur);
      }
      toast.success(`🎉 Your ${hours}h Pro trial is live — enjoy unlimited scans, AI meal plans & multi-store shopping!`, {
        duration: 8000,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Couldn't claim the trial — try again in a minute.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-4 mb-6 flex items-center gap-3 shadow-sm"
      style={{
        background: "linear-gradient(135deg,#FFF6E5 0%,#FFE5B5 100%)",
        border: "1px solid #E8C97A",
      }}
      data-testid="pro-trial-banner"
    >
      <div className="text-3xl flex-none" aria-hidden>🎁</div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Try Pro free for 48 hours
        </div>
        <div className="text-xs text-[#5C6B61] mt-0.5">
          One-tap trial · No card needed · Unlocks unlimited AI meal plans, pantry scans, multi-store shopping & more.
        </div>
      </div>
      <div className="flex items-center gap-2 flex-none">
        <button
          type="button"
          onClick={claim}
          disabled={busy}
          data-testid="claim-trial-btn"
          className="text-sm font-semibold rounded-full px-4 py-2 bg-[#3B5A3E] text-white hover:bg-[#2A4230] transition disabled:opacity-60"
        >
          {busy ? "Starting…" : "Claim now"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="claim-trial-dismiss"
          aria-label="Dismiss trial banner"
          className="text-[#8B9690] hover:text-[#2A332C] p-1"
          title="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
